/* KST Grouped Select (React 19) - Mobile Modal + Desktop Dropdown
   ENHANCED VERSION - Better status indicators + Online-only toggle
   Converted from Vue 2 KstGroupedSelect component
*/
import {
  useState,
  useRef,
  useCallback,
  useEffect,
  useMemo,
  memo,
  type CSSProperties,
} from 'react';
import { createPortal } from 'react-dom';
import { useTheme } from '@/contexts/ThemeContext';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

import { api } from '@/services/api-class';
import { getOfficerLabel } from '@/utils/legacy-utils';
import { getOfficerPluralLabel } from '@/utils/auth';

export interface GroupedDropdownlistItem {
  [key: string]: any;
}

interface StatusInfo {
  type: 'unknown' | 'hidden' | 'busy' | 'online' | 'offline';
  label: string;
  cssClass: string;
}

interface GroupedSection {
  name: string;
  items: GroupedDropdownlistItem[];
}

export interface GroupedDropdownlistProps {
  value: string | number | object | null;
  items: GroupedDropdownlistItem[];
  labelKey?: string;
  valueKey?: string;
  groupBy?: string;
  placeholder?: string;
  initialIsOfficerFilterOnlineOnly?: boolean;
  initialOpen?: boolean;
  onChange?: (value: string | number | object | null) => void;
  onItemChange?: (item: GroupedDropdownlistItem) => void;
  onFilterChange?: (info: { isOfficerFilterOnlineOnly: boolean }) => void;
  onCancel?: () => void;
  onTab?: (info: { shiftKey: boolean }) => void;
  onOpen?: () => void;
  onClose?: () => void;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function loadSavedPreference(): boolean {
  try {
    const saved = localStorage.getItem('kst-officer-filter-online-only');
    if (saved !== null) return saved === 'true';
  } catch {
    // localStorage not available
  }
  return false;
}

function getStatusInfo(item: GroupedDropdownlistItem): StatusInfo {
  if (item.manualStatus == null) {
    return { type: 'unknown', label: 'Unknown', cssClass: 'kst-gs__status--unknown' };
  }
  if (item.manualStatus === 2) {
    return { type: 'hidden', label: '', cssClass: 'kst-gs__status--hidden' };
  }
  if (item.callCard !== null && item.callCard !== undefined) {
    return { type: 'busy', label: 'Busy', cssClass: 'kst-gs__status--busy' };
  }
  if (item.manualStatus === 1) {
    return { type: 'online', label: 'Online', cssClass: 'kst-gs__status--online' };
  }
  return { type: 'offline', label: 'Offline', cssClass: 'kst-gs__status--offline' };
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const GroupedDropdownlist = memo(function GroupedDropdownlist({
  value,
  items = [],
  labelKey = 'label',
  valueKey = 'value',
  groupBy = 'group',
  placeholder = 'Select...',
  initialIsOfficerFilterOnlineOnly = false,
  initialOpen = false,
  onChange,
  onItemChange,
  onFilterChange,
  onCancel,
  onTab,
  onOpen,
  onClose,
}: GroupedDropdownlistProps) {
  const { theme } = useTheme();

  // ---------- State ----------

  const [open, setOpen] = useState(initialOpen);
  const [query, setQuery] = useState('');
  const [activeFlatIndex, setActiveFlatIndex] = useState(-1);
  const [isMobile, setIsMobile] = useState(false);
  const [justOpened, setJustOpened] = useState(false);
  const [dropdownStyle, setDropdownStyle] = useState<CSSProperties>({});
  const [dropdownFlipped, setDropdownFlipped] = useState(false);
  const [isOfficerFilterOnlineOnly, setIsOfficerFilterOnlineOnly] = useState(loadSavedPreference);
  const [isSavingPreference, setIsSavingPreference] = useState(false);

  // ---------- Refs ----------

  const rootRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const activeRowRef = useRef<HTMLDivElement>(null);
  const scrollYRef = useRef(0);
  const lastTouchYRef = useRef<number | null>(null);
  const justOpenedRef = useRef(false);
  const openRef = useRef(false);
  const flipDecisionLockedRef = useRef(false);
  const preferenceLoadedFromApiRef = useRef(false);
  const hasBeenOpenRef = useRef(initialOpen);

  // Keep refs in sync with state (avoids stale closures in event listeners)
  openRef.current = open;
  justOpenedRef.current = justOpened;

  // ---------- Computed values ----------

  const selectedItem = useMemo(() => {
    if (!items || items.length === 0) return null;
    for (let i = 0; i < items.length; i++) {
      if (items[i][valueKey] === value) return items[i];
    }
    return null;
  }, [items, valueKey, value]);

  const onlineCount = useMemo(() => {
    if (!items || items.length === 0) return 0;
    return items.filter((it) => it.manualStatus === 1 && !it.callCard).length;
  }, [items]);

  const totalCount = useMemo(() => (items ? items.length : 0), [items]);

  const searchFiltered = useMemo(() => {
    if (!items || items.length === 0) return [];
    const q = (query || '').trim().toLowerCase();
    if (!q) return items.slice();
    return items.filter((it) => {
      const lbl = String(it[labelKey] || '').toLowerCase();
      const grp = String(it[groupBy] || '').toLowerCase();
      return lbl.indexOf(q) !== -1 || grp.indexOf(q) !== -1;
    });
  }, [items, query, labelKey, groupBy]);

  const filtered = useMemo(() => {
    if (!isOfficerFilterOnlineOnly) return searchFiltered;
    return searchFiltered.filter((it) => it.manualStatus === 1 && !it.callCard);
  }, [searchFiltered, isOfficerFilterOnlineOnly]);

  const grouped = useMemo((): GroupedSection[] => {
    if (!filtered || filtered.length === 0) return [];
    const map: Record<string, GroupedDropdownlistItem[]> = {};

    for (let i = 0; i < filtered.length; i++) {
      const it = filtered[i];
      const key = it[groupBy] || 'Other';
      if (!map[key]) map[key] = [];
      map[key].push(it);
    }

    const groups = Object.keys(map).sort();
    const out: GroupedSection[] = [];
    for (let g = 0; g < groups.length; g++) {
      const name = groups[g];
      const arr = map[name].slice().sort((a, b) => {
        const la = String(a[labelKey] || '');
        const lb = String(b[labelKey] || '');
        return la.localeCompare(lb, undefined, { numeric: true, sensitivity: 'base' });
      });
      out.push({ name, items: arr });
    }
    return out;
  }, [filtered, groupBy, labelKey]);

  const flatVisible = useMemo(() => {
    const acc: GroupedDropdownlistItem[] = [];
    for (let i = 0; i < grouped.length; i++) {
      for (let j = 0; j < grouped[i].items.length; j++) {
        acc.push(grouped[i].items[j]);
      }
    }
    return acc;
  }, [grouped]);

  const displayText = useMemo(
    () => (selectedItem ? selectedItem[labelKey] : ''),
    [selectedItem, labelKey],
  );

  const hasValue = !!displayText;

  // ---------- Refs for stale-closure-prone values ----------

  const flatVisibleRef = useRef(flatVisible);
  flatVisibleRef.current = flatVisible;

  const activeFlatIndexRef = useRef(activeFlatIndex);
  activeFlatIndexRef.current = activeFlatIndex;

  const filteredRef = useRef(filtered);
  filteredRef.current = filtered;

  // ---------- Mobile check ----------

  useEffect(() => {
    const mobile =
      /iPhone|iPad|iPod|Android/i.test(navigator.userAgent) || window.innerWidth <= 768;
    setIsMobile(mobile);
  }, []);

  // ---------- Preference: sync from prop ----------

  useEffect(() => {
    if (typeof initialIsOfficerFilterOnlineOnly === 'boolean') {
      setIsOfficerFilterOnlineOnly(initialIsOfficerFilterOnlineOnly);
      preferenceLoadedFromApiRef.current = true;
    }
  }, [initialIsOfficerFilterOnlineOnly]);

  // ---------- Preference: load from API on mount ----------

  useEffect(() => {
    if (preferenceLoadedFromApiRef.current) return;

    if (typeof api !== 'undefined' && api.getPersonSettings) {
      api.getPersonSettings(
        (result: any) => {
          if (
            result &&
            result.settings &&
            typeof result.settings.isOfficerFilterOnlineOnly === 'boolean'
          ) {
            setIsOfficerFilterOnlineOnly(result.settings.isOfficerFilterOnlineOnly);
            preferenceLoadedFromApiRef.current = true;
            try {
              localStorage.setItem(
                'kst-officer-filter-online-only',
                result.settings.isOfficerFilterOnlineOnly ? 'true' : 'false',
              );
            } catch {
              // localStorage not available
            }
          }
        },
        () => {
          // localStorage fallback already loaded in initial state
        },
      );
    }
  }, []);

  // ---------- Save filter preference ----------

  const saveFilterPreference = useCallback((onlineOnly: boolean) => {
    setIsSavingPreference(true);

    try {
      localStorage.setItem('kst-officer-filter-online-only', onlineOnly ? 'true' : 'false');
    } catch {
      // localStorage not available
    }

    if (typeof api !== 'undefined' && api.updateOfficerFilterPreference) {
      api.updateOfficerFilterPreference(
        onlineOnly,
        () => setIsSavingPreference(false),
        () => setIsSavingPreference(false),
      );
    } else {
      setIsSavingPreference(false);
    }
  }, []);

  const dropdownRafRef = useRef(0);
  const updateDropdownPosition = useCallback(() => {
    if (!openRef.current) return;

    // rAF-throttle: cancel any pending frame to avoid queuing multiples
    if (dropdownRafRef.current) cancelAnimationFrame(dropdownRafRef.current);
    dropdownRafRef.current = requestAnimationFrame(() => {
      dropdownRafRef.current = 0;
      const control = rootRef.current?.querySelector('.kst-gs__control');
      if (!control) return;

      const rect = control.getBoundingClientRect();
      const viewportHeight = window.innerHeight;

      const dropdownHeight = 380;
      const spaceBelow = viewportHeight - rect.bottom;
      const spaceAbove = rect.top;
      const padding = 8; // keep some distance from viewport edge

      let shouldFlip = false;
      if (spaceBelow < dropdownHeight && spaceAbove > spaceBelow) {
        shouldFlip = true;
      }

      setDropdownFlipped(shouldFlip);

      // Apply position: fixed styles directly to the dropdown div (matches Vue behaviour).
      // Also clamp maxHeight so the dropdown never extends outside the viewport.
      if (shouldFlip) {
        const maxH = Math.max(rect.top - padding, 120);
        setDropdownStyle({
          position: 'fixed',
          bottom: (viewportHeight - rect.top + 4) + 'px',
          left: rect.left + 'px',
          width: Math.max(rect.width, 300) + 'px',
          zIndex: 9999,
          top: 'auto',
          maxHeight: maxH + 'px',
        });
      } else {
        const maxH = Math.max(viewportHeight - rect.bottom - padding, 120);
        setDropdownStyle({
          position: 'fixed',
          top: (rect.bottom + 4) + 'px',
          left: rect.left + 'px',
          width: Math.max(rect.width, 300) + 'px',
          zIndex: 9999,
          bottom: 'auto',
          maxHeight: maxH + 'px',
        });
      }
    });
  }, []);

  // ---------- Scroll active item into view ----------

  const scrollActiveIntoView = useCallback(() => {
    requestAnimationFrame(() => {
      const list = listRef.current;
      const active = activeRowRef.current;
      if (!list || !active) return;
      const aTop = active.offsetTop;
      const aBottom = aTop + active.offsetHeight;
      const vTop = list.scrollTop;
      const vBottom = vTop + list.clientHeight;
      if (aTop < vTop) list.scrollTop = aTop;
      else if (aBottom > vBottom) list.scrollTop = aBottom - list.clientHeight;
    });
  }, []);

  // Track whether the next activeFlatIndex change should scroll into view
  // (only for keyboard navigation, not mouse hover)
  const scrollOnNextIndexChange = useRef(false);

  useEffect(() => {
    if (open && scrollOnNextIndexChange.current) {
      scrollOnNextIndexChange.current = false;
      scrollActiveIntoView();
    }
  }, [activeFlatIndex, open, scrollActiveIntoView]);

  // ---------- Actions ----------

  // Blur the search input before closing to prevent browser scroll-to-top
  // when the focused element is removed from the DOM.
  const blurBeforeClose = useCallback(() => {
    searchRef.current?.blur();
  }, []);

  const doClose = useCallback(() => {
    blurBeforeClose();
    setOpen(false);
  }, [blurBeforeClose]);

  const select = useCallback(
    (item: GroupedDropdownlistItem) => {
      blurBeforeClose();
      onChange?.(item[valueKey]);
      onItemChange?.(item);
      setOpen(false);
    },
    [blurBeforeClose, onChange, onItemChange, valueKey],
  );

  // ---------- Toggle online-only filter ----------

  const toggleOnlineOnly = useCallback(
    (forceValue?: boolean) => {
      setIsOfficerFilterOnlineOnly((prev) => {
        const next = forceValue !== undefined ? forceValue : !prev;
        // Reset active index after filter change
        // We defer this to next tick so flatVisible is updated
        requestAnimationFrame(() => {
          const flat = flatVisibleRef.current;
          setActiveFlatIndex(flat.length ? 0 : -1);
          scrollActiveIntoView();
        });

        onFilterChange?.({ isOfficerFilterOnlineOnly: next });
        saveFilterPreference(next);
        return next;
      });
    },
    [onFilterChange, saveFilterPreference, scrollActiveIntoView],
  );

  // ---------- Keyboard ----------

  const onKeyDownControl = useCallback((e: React.KeyboardEvent) => {
    const k = e.key;
    if (!openRef.current && (k === 'ArrowDown' || k === 'Enter' || k === ' ')) {
      e.preventDefault();
      setOpen(true);
    }
  }, []);

  const onKeyDownList = useCallback(
    (e: React.KeyboardEvent) => {
      const k = e.key;
      const currentFlat = flatVisibleRef.current;
      const currentIdx = activeFlatIndexRef.current;

      if (k === 'Escape') {
        e.preventDefault();
        blurBeforeClose();
        onCancel?.();
        setOpen(false);
        return;
      }

      if (k === 'Tab') {
        e.preventDefault();
        blurBeforeClose();

        const item = currentFlat[currentIdx];
        if (item) {
          onChange?.(item[valueKey]);
          onItemChange?.(item);
          setOpen(false);
        } else {
          onCancel?.();
          setOpen(false);
        }

        onTab?.({ shiftKey: e.shiftKey });
        return;
      }

      if (!currentFlat.length) return;

      if (k === 'ArrowDown') {
        e.preventDefault();
        scrollOnNextIndexChange.current = true;
        setActiveFlatIndex(Math.min(currentFlat.length - 1, currentIdx + 1));
      } else if (k === 'ArrowUp') {
        e.preventDefault();
        scrollOnNextIndexChange.current = true;
        setActiveFlatIndex(Math.max(0, currentIdx - 1));
      } else if (k === 'Home') {
        e.preventDefault();
        scrollOnNextIndexChange.current = true;
        setActiveFlatIndex(0);
      } else if (k === 'End') {
        e.preventDefault();
        scrollOnNextIndexChange.current = true;
        setActiveFlatIndex(currentFlat.length - 1);
      } else if (k === 'Enter') {
        e.preventDefault();
        const item = currentFlat[currentIdx];
        if (item) {
          blurBeforeClose();
          onChange?.(item[valueKey]);
          onItemChange?.(item);
          setOpen(false);
        }
      }
    },
    [blurBeforeClose, onCancel, onChange, onItemChange, onTab, valueKey],
  );

  // ---------- Toggle open ----------

  const toggleOpen = useCallback((e?: React.MouseEvent | React.TouchEvent) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    setOpen((prev) => !prev);
  }, []);

  // ---------- Click outside ----------

  const onClickOutside = useCallback((e: MouseEvent | TouchEvent) => {
    if (!openRef.current) return;
    if (justOpenedRef.current) return;

    // Ignore clicks on browser scrollbars (outside the document client area)
    if (e instanceof MouseEvent) {
      if (e.clientX >= document.documentElement.clientWidth) return;
      if (e.clientY >= document.documentElement.clientHeight) return;
    }

    const target = e.target as Node;
    const inComponent = rootRef.current?.contains(target);

    // Read mobile state from DOM to avoid stale closure
    const mobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent) || window.innerWidth <= 768;

    if (mobile) {
      const modal = document.querySelector('.kst-gs__modal');
      const inModal = modal?.contains(target);
      if (!inComponent && !inModal) {
        searchRef.current?.blur();
        setOpen(false);
      }
    } else {
      if (!inComponent) {
        searchRef.current?.blur();
        setOpen(false);
      }
    }
  }, []);

  // ---------- Touch scroll chaining prevention ----------

  const resetTouchTracking = useCallback(() => {
    lastTouchYRef.current = null;
  }, []);

  const preventScrollChaining = useCallback((e: React.TouchEvent) => {
    const list = listRef.current;
    if (!list) return;

    const scrollTop = list.scrollTop;
    const scrollHeight = list.scrollHeight;
    const clientHeight = list.clientHeight;

    const isAtTop = scrollTop === 0;
    const isAtBottom = scrollTop + clientHeight >= scrollHeight;

    const touchY = e.touches[0].clientY;
    if (lastTouchYRef.current === null) {
      lastTouchYRef.current = touchY;
      return;
    }

    const deltaY = lastTouchYRef.current - touchY;
    const isScrollingUp = deltaY < 0;
    const isScrollingDown = deltaY > 0;

    if ((isAtTop && isScrollingUp) || (isAtBottom && isScrollingDown)) {
      e.preventDefault();
    }

    lastTouchYRef.current = touchY;
  }, []);

  // ---------- Backdrop click ----------

  const onBackdropClick = useCallback((e: React.MouseEvent) => {
    if ((e.target as HTMLElement).classList.contains('kst-gs__modal-backdrop')) {
      searchRef.current?.blur();
      setOpen(false);
    }
  }, []);

  // ---------- Effect: open/close lifecycle ----------

  useEffect(() => {
    if (open) {
      hasBeenOpenRef.current = true;
      onOpen?.();
      setJustOpened(true);
      justOpenedRef.current = true;
      flipDecisionLockedRef.current = false;

      // Set active index to selected item or first item
      const currentFlat = flatVisibleRef.current;
      let idx = -1;
      for (let i = 0; i < currentFlat.length; i++) {
        if (currentFlat[i][valueKey] === value) {
          idx = i;
          break;
        }
      }
      scrollOnNextIndexChange.current = true;
      setActiveFlatIndex(idx >= 0 ? idx : currentFlat.length ? 0 : -1);

      const mobile =
        /iPhone|iPad|iPod|Android/i.test(navigator.userAgent) || window.innerWidth <= 768;

      if (!mobile) {
        // Desktop: position and focus
        requestAnimationFrame(() => {
          updateDropdownPosition();
          flipDecisionLockedRef.current = true;

          requestAnimationFrame(() => {
            searchRef.current?.focus();
          });
        });

        window.addEventListener('scroll', updateDropdownPosition, true);
        window.addEventListener('resize', updateDropdownPosition);
      }

      // Lock body scroll on mobile
      if (mobile) {
        scrollYRef.current = window.scrollY || window.pageYOffset;
        document.body.style.position = 'fixed';
        document.body.style.top = '-' + scrollYRef.current + 'px';
        document.body.style.left = '0';
        document.body.style.right = '0';
        document.body.style.overflow = 'hidden';
      }

      // Clear justOpened flag after delay
      const timer = setTimeout(() => {
        setJustOpened(false);
        justOpenedRef.current = false;
      }, 200);

      return () => clearTimeout(timer);
    } else {
      // Skip everything on initial mount — only act when transitioning from open→closed
      if (hasBeenOpenRef.current) {
        onClose?.();

        setQuery('');
        setActiveFlatIndex(-1);
        setDropdownFlipped(false);
        flipDecisionLockedRef.current = false;
        setJustOpened(false);
        justOpenedRef.current = false;

        const mobile =
          /iPhone|iPad|iPod|Android/i.test(navigator.userAgent) || window.innerWidth <= 768;

        if (!mobile) {
          setDropdownStyle({});
          window.removeEventListener('scroll', updateDropdownPosition, true);
          window.removeEventListener('resize', updateDropdownPosition);
        }

        // Restore body scroll on mobile
        if (mobile) {
          document.body.style.position = '';
          document.body.style.top = '';
          document.body.style.left = '';
          document.body.style.right = '';
          document.body.style.overflow = '';
          window.scrollTo(0, scrollYRef.current);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // ---------- Effect: register click-outside listeners on mount ----------

  useEffect(() => {
    const timer = setTimeout(() => {
      document.addEventListener('mouseup', onClickOutside as EventListener, true);
      document.addEventListener('touchend', onClickOutside as EventListener, {
        passive: true,
        capture: true,
      });
    }, 100);

    return () => {
      clearTimeout(timer);
      document.removeEventListener('mouseup', onClickOutside as EventListener, true);
      document.removeEventListener('touchend', onClickOutside as EventListener, true);
    };
  }, [onClickOutside]);

  // ---------- Effect: cleanup on unmount ----------

  useEffect(() => {
    return () => {
      window.removeEventListener('scroll', updateDropdownPosition, true);
      window.removeEventListener('resize', updateDropdownPosition);

      // Ensure body scroll is restored
      const wasLocked = document.body.style.position === 'fixed';
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.left = '';
      document.body.style.right = '';
      document.body.style.overflow = '';
      if (wasLocked) {
        window.scrollTo(0, scrollYRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---------- Search input handler ----------

  const onSearchInput = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const val = e.target.value;
      setQuery(val);

      // Compute new filtered inline so we can reset activeFlatIndex immediately
      const q = val.trim().toLowerCase();
      let newSearchFiltered: GroupedDropdownlistItem[];
      if (!q) {
        newSearchFiltered = items.slice();
      } else {
        newSearchFiltered = items.filter((it) => {
          const lbl = String(it[labelKey] || '').toLowerCase();
          const grp = String(it[groupBy] || '').toLowerCase();
          return lbl.indexOf(q) !== -1 || grp.indexOf(q) !== -1;
        });
      }

      // Apply online filter if active
      let newFiltered = newSearchFiltered;
      if (isOfficerFilterOnlineOnly) {
        newFiltered = newSearchFiltered.filter(
          (it) => it.manualStatus === 1 && !it.callCard,
        );
      }

      scrollOnNextIndexChange.current = true;
      setActiveFlatIndex(newFiltered.length ? 0 : -1);
    },
    [items, labelKey, groupBy, isOfficerFilterOnlineOnly],
  );

  // ---------- Render: filter toggle ----------

  const renderFilterToggle = (isMobileToggle: boolean) => {
    const containerClass = isMobileToggle
      ? 'kst-gs__filter-toggle-mobile'
      : 'kst-gs__filter-toggle';

    return (
      <div className={containerClass}>
        <div className="kst-gs__toggle-group">
          <button
            className={`kst-gs__toggle-btn${!isOfficerFilterOnlineOnly ? ' kst-gs__toggle-btn--active' : ''}`}
            type="button"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              if (isOfficerFilterOnlineOnly) {
                toggleOnlineOnly(false);
              }
            }}
          >
            <span className="kst-gs__toggle-label">All</span>
            <span className="kst-gs__toggle-count">{String(totalCount)}</span>
          </button>
          <button
            className={`kst-gs__toggle-btn${isOfficerFilterOnlineOnly ? ' kst-gs__toggle-btn--active kst-gs__toggle-btn--online' : ''}`}
            type="button"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              if (!isOfficerFilterOnlineOnly) {
                toggleOnlineOnly(true);
              }
            }}
          >
            <span className="kst-gs__toggle-indicator kst-gs__status--online" />
            <span className="kst-gs__toggle-label">Online</span>
            <span className="kst-gs__toggle-count">{String(onlineCount)}</span>
          </button>
        </div>
      </div>
    );
  };

  // ---------- Render: list content ----------

  const renderListContent = () => {
    if (!grouped.length) {
      const emptyMessage = isOfficerFilterOnlineOnly
        ? 'No online officers found'
        : 'No results';

      return (
        <div className="kst-gs__item kst-gs__item--empty" aria-disabled="true">
          {emptyMessage}
        </div>
      );
    }

    const nodes: React.ReactNode[] = [];
    let flatIdx = 0;

    for (let g = 0; g < grouped.length; g++) {
      const group = grouped[g];

      nodes.push(
        <div key={`group-${group.name}`} className="kst-gs__group">
          {group.name}
        </div>,
      );

      for (let j = 0; j < group.items.length; j++) {
        const item = group.items[j];
        const currentFlatIdx = flatIdx;
        const isSel = value === item[valueKey];
        const isAct = currentFlatIdx === activeFlatIndex;
        const statusInfo = getStatusInfo(item);

        nodes.push(
          <div
            key={`item-${item[valueKey] ?? currentFlatIdx}`}
            className={`kst-gs__item${isAct ? ' kst-gs__item--active' : ''}`}
            aria-selected={isSel ? 'true' : 'false'}
            role="option"
            ref={isAct ? activeRowRef : undefined}
            onClick={() => select(item)}
            onMouseEnter={() => setActiveFlatIndex(currentFlatIdx)}
          >
            {/* Status indicator with enhanced styling */}
            <span
              className={`kst-gs__status ${statusInfo.cssClass}`}
              title={statusInfo.label}
              aria-label={statusInfo.label}
            >
              {statusInfo.type !== 'hidden' && <span className="kst-gs__status-inner" />}
            </span>
            {/* Label */}
            <span className="kst-gs__label">{item[labelKey]}</span>
            {/* Status text badge */}
            {statusInfo.type !== 'hidden' && statusInfo.type !== 'unknown' && (
              <span className={`kst-gs__status-badge kst-gs__status-badge--${statusInfo.type}`}>
                {statusInfo.label}
              </span>
            )}
          </div>,
        );

        flatIdx++;
      }
    }

    return nodes;
  };

  // ---------- Desktop dropdown (rendered inline with position: fixed style) ----------

  const desktopDropdown =
    !isMobile && open ? (
      <div
        className={`kst-gs__dropdown${dropdownFlipped ? ' kst-gs__dropdown--flipped' : ''}`}
        style={dropdownStyle}
        role="listbox"
      >
        {/* Search input */}
        <div className="kst-gs__search">
          <input
            ref={searchRef}
            className="kst-gs__search-input"
            value={query}
            type="text"
            placeholder="Search..."
            aria-label="Filter options"
            inputMode="text"
            onChange={onSearchInput}
            onKeyDown={onKeyDownList}
          />
        </div>
        {/* Filter toggle */}
        {renderFilterToggle(false)}
        {/* List */}
        <div className="kst-gs__list" ref={listRef} onKeyDown={onKeyDownList}>
          {renderListContent()}
        </div>
      </div>
    ) : null;

  // ---------- Mobile modal ----------

  let officerLabel = 'Officer';
  let officerPluralLabel = 'Officers';
  try {
    officerLabel = getOfficerLabel();
    officerPluralLabel = getOfficerPluralLabel();
  } catch {
    // Functions not available yet
  }

  const mobileModal =
    isMobile && open ? (
      <div className="kst-gs__modal-backdrop" onClick={onBackdropClick}>
        <div className="kst-gs__modal" onClick={(e) => e.stopPropagation()}>
          {/* Modal Header */}
          <div className="kst-gs__modal-header">
            <div className="kst-gs__modal-title">{`Select ${officerLabel}`}</div>
            <button
              className="kst-gs__modal-close"
              type="button"
              aria-label="Close"
              onClick={doClose}
            >
              &times;
            </button>
          </div>
          {/* Modal Search */}
          <div className="kst-gs__modal-search">
            <input
              ref={searchRef}
              className="kst-gs__modal-search-input"
              value={query}
              type="text"
              placeholder={`Search ${officerPluralLabel.toLowerCase()}...`}
              aria-label="Filter options"
              inputMode="text"
              onChange={onSearchInput}
              onKeyDown={onKeyDownList}
            />
          </div>
          {/* Filter toggle for mobile */}
          {renderFilterToggle(true)}
          {/* Modal List */}
          <div
            className="kst-gs__modal-list"
            ref={listRef}
            onTouchStart={resetTouchTracking}
            onTouchMove={preventScrollChaining}
            onTouchEnd={resetTouchTracking}
          >
            {renderListContent()}
          </div>
        </div>
      </div>
    ) : null;

  // Render the mobile modal into a portal on document.body so it escapes any
  // ancestor that clips/constrains fixed positioning (e.g. a modal dialog body
  // with overflow:auto / -webkit-overflow-scrolling on iOS). The theme class is
  // re-applied on the portal wrapper since the themed root is left behind.
  const mobilePortal = mobileModal
    ? createPortal(
        <div className={'kst-gs' + (theme ? ' ' + theme : '')}>{mobileModal}</div>,
        document.body
      )
    : null;

  // ---------- Control classes ----------

  const controlClasses = ['kst-gs__control', open ? 'kst-gs__control--open' : '']
    .filter(Boolean)
    .join(' ');

  // ---------- Main render ----------

  return (
    <div className={`kst-gs${theme ? ' ' + theme : ''}`} ref={rootRef}>
      <div
        className={controlClasses}
        role="combobox"
        aria-expanded={open ? 'true' : 'false'}
        tabIndex={0}
        onClick={(e) => {
          e.stopPropagation();
          toggleOpen(e);
        }}
        onTouchEnd={(e) => {
          e.preventDefault();
          e.stopPropagation();
          toggleOpen(e);
        }}
        onKeyDown={onKeyDownControl}
      >
        <div className={`kst-gs__value${!hasValue ? ' kst-gs__value--placeholder' : ''}`}>
          {hasValue ? displayText : placeholder}
        </div>
        <div className="kst-gs__caret" />
      </div>
      {desktopDropdown}
      {mobilePortal}
    </div>
  );
});

(window as any).KstGroupedSelectReact = GroupedDropdownlist;
